<template>
  <div class="procedures-page">
    <div
      v-if="booksState.books.length > 0 || searchQuery.trim() !== ''"
      class="procedures-page-shell procedures-page-shell--search"
    >
      <ProceduresSearchBar
        v-model="searchQuery"
        placeholder="Search in Procedures or ask for anything"
        class="procedures-page-searchbar"
        :withContainerBackground="true"
      />
    </div>

    <!-- Only this area swaps between BookPanel and the book editor -->
    <div class="procedures-page-shell procedures-page-shell--content">
      <exiq-book-content-edit-card
        v-if="activeBookId"
        :key="String(activeBookId)"
        ref="bookEditorRef"
        @breadcrumb-click="handleBreadcrumbClick"
        @add-chapter="handleAddChapter"
      ></exiq-book-content-edit-card>

      <BookPanel
        v-else
        :books="booksState.books"
        :loading="booksState.loading"
        :search-query="searchQuery"
        :status-filter="statusFilter"
        :ordering="ordering"
        :total-count="booksTotal"
        :current-page="currentPage"
        :items-per-page="ITEMS_PER_PAGE"
        @add-book="bookActions.openAddDialog"
        @open-book="bookActions.openBook"
        @update:status-filter="handleStatusFilterChange"
        @update:ordering="handleOrderingChange"
        @page-change="handlePageChange"
      />
    </div>

    <!-- dialogs (always present) -->
    <div>
      <AddBookDialog
        v-show="bookState.showAddBookDialog"
        :value="bookState.showAddBookDialog"
        :errorMessage="bookState.errorMessage"
        @onAdd="bookActions.addBook"
        @clearError="bookActions.clearError"
        @input="bookState.showAddBookDialog = $event"
      />

      <AddChapterDialog
        v-show="chapterState.showAddChapterDialog"
        :value="chapterState.showAddChapterDialog"
        :errorMessage="chapterState.errorMessage"
        @onSave="chapterActions.addChapter"
        @clearError="chapterActions.clearError"
        @input="chapterState.showAddChapterDialog = $event"
      />
    </div>
    <!-- end dialogs -->
  </div>
</template>
<script setup>
import ProceduresSearchBar from "@components/ProceduresSearchBar/ProceduresSearchBar.vue";
import BookPanel from "@components/BookStack/BookPanel.vue";
import AddBookDialog from "@components/AddBookDialog/AddBookDialog.vue";
import AddChapterDialog from "@components/AddChapterDialog/AddChapterDialog.vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router/composables";
import { createBook, createBookChapter, getBookById, getBookChapters, getBooks } from "@/api";
import { formatStatusDate } from "@components/utils/formatStatusDate";

const router = useRouter();

const getProceduresBasePath = () => {
  const currentPath = String(router?.currentRoute?.path || "");
  return currentPath.startsWith("/organization/procedures")
    ? "/organization/procedures"
    : "/procedures";
};

const getBookRouteTarget = (bookId) => {
  const currentPath = String(router?.currentRoute?.path || "");
  if (currentPath.startsWith("/organization/procedures")) {
    return {
      name: "NewOrganizationProceduresBook",
      params: { tab: "procedures", bookId: String(bookId) },
    };
  }

  return {
    name: "ProceduresBook",
    params: { bookId: String(bookId) },
  };
};

const bookEditorRef = ref(null);
const bookDetailState = ref({
  loading: false,
  errorMessage: "",
  book: null,
  chapters: [],
  procedures: [],
});

// vue-router 3 exposes a reactive `currentRoute` (Vue observable). Using it
// makes bookId updates work with browser back/forward reliably.
const routeSnapshot = ref(router?.currentRoute || null);
watch(
  () => router?.currentRoute?.fullPath,
  () => {
    routeSnapshot.value = router?.currentRoute || null;
  },
  { immediate: true }
);

const activeBookId = computed(() => {
  const raw = routeSnapshot.value?.params?.bookId;
  if (raw === undefined || raw === null) return null;
  const value = String(raw).trim();
  return value ? value : null;
});

const bookState = ref({
  showAddBookDialog: false,
  errorMessage: "",
});

const chapterState = ref({
  showAddChapterDialog: false,
  errorMessage: "",
});

const ITEMS_PER_PAGE = 20;
const DEFAULT_ORDERING = "name";

const booksState = ref({
  books: [],
  loading: true,
});
const hasFetchedBooks = ref(false);

const booksTotal = ref(0);
const currentPage = ref(1);

const searchQuery = ref("");
const statusFilter = ref("");
const ordering = ref(DEFAULT_ORDERING);

let searchDebounceTimer = null;

const getCurrentAccountId = () => {
  const raw = localStorage.getItem("currentAccountID");
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (e) {
    return raw;
  }
};

const mapBookToCard = (book) => ({
  ...book,
  name: book?.name ?? "",
  subtext: book?.description ?? book?.subtext ?? "",
  status: String(book?.status ?? "").toLowerCase(),
  created_on: formatStatusDate(book?.created_on ?? book?.createdAt),
  updated_on: formatStatusDate(book?.updated_on ?? book?.updatedAt),
});

const loadBooks = async ({ page = 1, name = "" } = {}) => {
  const accountId = getCurrentAccountId();
  if (!accountId) {
    booksState.value.books = [];
    booksState.value.loading = false;
    booksTotal.value = 0;
    return;
  }

  booksState.value.loading = true;
  try {
    const params = {
      limit: ITEMS_PER_PAGE,
      offset: (page - 1) * ITEMS_PER_PAGE,
    };
    if (name && name.trim()) {
      params.name = name.trim();
    }
    if (statusFilter.value) {
      params.status = statusFilter.value;
    }
    if (ordering.value !== DEFAULT_ORDERING) {
      params.ordering = ordering.value;
    }
    const response = await getBooks(accountId, params);
    const data = response?.data;
    const list = Array.isArray(data) ? data : (data?.results ?? []);
    const count = Array.isArray(data) ? list.length : (data?.count ?? list.length);
    booksState.value.books = list.map(mapBookToCard);
    booksTotal.value = count;
    currentPage.value = page;
  } finally {
    booksState.value.loading = false;
    hasFetchedBooks.value = true;
  }
};

const handlePageChange = (page) => {
  loadBooks({ page, name: searchQuery.value });
};

const reloadFirstPage = async () => {
  try {
    await loadBooks({ page: 1, name: searchQuery.value });
  } catch (e) {
    console.error("error loading books: ", e);
  }
};

const handleStatusFilterChange = (value) => {
  statusFilter.value = value;
  return reloadFirstPage();
};

const handleOrderingChange = (value) => {
  ordering.value = value;
  return reloadFirstPage();
};

watch(searchQuery, (val) => {
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    loadBooks({ page: 1, name: val });
  }, 400);
});

const loadChapters = async (bookId) => {
  const accountId = getCurrentAccountId();
  if (!accountId || !bookId) {
    bookDetailState.value.chapters = [];
    return;
  }

  const response = await getBookChapters(accountId, bookId);
  const data = response?.data;
  const list = Array.isArray(data) ? data : (data?.results ?? []);
  bookDetailState.value.chapters = list;
};

const syncBookEditorProps = async () => {
  await nextTick();
  const el = bookEditorRef.value;
  const book = bookDetailState.value.book;
  if (!el || !book) return;

  // Set DOM properties on the Web Component (not attributes).
  el.location = "Book";
  el.bookName = book?.name ?? "";
  el.chapterName = "";
  el.procedureName = "";
  el.notifications = 0;
  el.debug = false;
  el.chapters = Array.isArray(bookDetailState.value.chapters) ? bookDetailState.value.chapters : [];
  el.procedures = Array.isArray(bookDetailState.value.procedures)
    ? bookDetailState.value.procedures
    : [];
};

const loadBookDetail = async (bookId) => {
  const accountId = getCurrentAccountId();
  if (!accountId) return;

  bookDetailState.value.loading = true;
  bookDetailState.value.errorMessage = "";
  try {
    const response = await getBookById(accountId, bookId);
    const data = response?.data ?? null;
    bookDetailState.value.book = data ? mapBookToCard(data) : { id: bookId, name: "" };
    bookDetailState.value.procedures = Array.isArray(data?.procedures) ? data.procedures : [];
    await loadChapters(bookId);
    await syncBookEditorProps();
  } catch (e) {
    const apiMessage = e?.response?.data?.detail || e?.message || "Unable to load book.";
    bookDetailState.value.errorMessage = String(apiMessage);

    // Fallback: if we already have the list, try to derive the name.
    const fromList = (booksState.value.books || []).find((b) => String(b?.id) === String(bookId));
    bookDetailState.value.book = fromList || { id: bookId, name: "" };
    bookDetailState.value.chapters = [];
    bookDetailState.value.procedures = [];
    await syncBookEditorProps();
  } finally {
    bookDetailState.value.loading = false;
  }
};

watch(
  activeBookId,
  async (bookId) => {
    if (!bookId) {
      bookDetailState.value.book = null;
      bookDetailState.value.chapters = [];
      bookDetailState.value.procedures = [];
      return;
    }
    await loadBookDetail(bookId);
  },
  { immediate: true }
);

watch(bookEditorRef, async (el) => {
  if (el && activeBookId.value && bookDetailState.value.book) {
    await syncBookEditorProps();
  }
});

const handleBreadcrumbClick = async (event) => {
  // Webcomponents dispatch bubbling CustomEvents; stop them here to avoid
  // other listeners triggering competing navigations (Navigation cancelled).
  try {
    event?.stopImmediatePropagation?.();
  } catch (_) {}
  try {
    event?.stopPropagation?.();
  } catch (_) {}
  try {
    event?.preventDefault?.();
  } catch (_) {}

  const targetPath = getProceduresBasePath();
  if (router?.currentRoute?.path === targetPath) return;

  try {
    // Use replace to keep browser history clean (breadcrumb = go back).
    await router.replace(targetPath);
  } catch (err) {
    const name = err?.name;
    if (
      name === "NavigationDuplicated" ||
      name === "NavigationCancelled" ||
      name === "NavigationAborted"
    ) {
      return;
    }
    // Only log unexpected navigation errors
    // eslint-disable-next-line no-console
    console.error("breadcrumb navigation error:", err);
  }
};

const handleAddChapter = (event) => {
  // Webcomponent emits a bubbling event; stop it to avoid duplicates.
  try {
    event?.stopImmediatePropagation?.();
  } catch (_) {}
  try {
    event?.stopPropagation?.();
  } catch (_) {}
  chapterActions.openAddDialog();
};

const bookActions = {
  openAddDialog: () => {
    bookState.value.showAddBookDialog = true;
    bookState.value.errorMessage = "";
  },
  closeAddDialog: () => {
    bookState.value.showAddBookDialog = false;
  },
  clearError: () => {
    bookState.value.errorMessage = "";
  },
  openBook: (book) => {
    const id = book?.id;
    if (id === undefined || id === null) return;
    router.push(getBookRouteTarget(id)).catch((err) => {
      const name = err?.name;
      if (
        name === "NavigationDuplicated" ||
        name === "NavigationCancelled" ||
        name === "NavigationAborted"
      ) {
        return;
      }
      // eslint-disable-next-line no-console
      console.error("openBook navigation error:", err);
    });
  },
  addBook: async (bookName) => {
    const accountId = getCurrentAccountId();
    if (!accountId) return;

    try {
      const payload = {
        name: bookName,
        account: accountId,
      };
      await createBook(accountId, payload);
      bookActions.closeAddDialog();
      statusFilter.value = "";
      searchQuery.value = bookName;
      // Cancel the debounce the watch just scheduled so we don't double-fetch.
      clearTimeout(searchDebounceTimer);
      await loadBooks({ page: 1, name: bookName });
    } catch (e) {
      const responseData = e?.response?.data;
      const nonFieldErrors = responseData?.non_field_errors;
      const isUniqueConstraint =
        Array.isArray(nonFieldErrors) &&
        nonFieldErrors.some(
          (msg) => typeof msg === "string" && msg.toLowerCase().includes("unique")
        );

      const apiMessage = isUniqueConstraint
        ? "Name already exists. Only Unique names are allowed."
        : responseData?.name?.[0] || responseData?.detail || e?.message || "Unable to add book.";

      bookState.value.errorMessage = String(apiMessage);
    }
  },
};

const chapterActions = {
  openAddDialog: () => {
    if (!activeBookId.value) return;
    chapterState.value.showAddChapterDialog = true;
    chapterState.value.errorMessage = "";
  },
  closeAddDialog: () => {
    chapterState.value.showAddChapterDialog = false;
  },
  clearError: () => {
    chapterState.value.errorMessage = "";
  },
  addChapter: async (chapterName) => {
    const accountId = getCurrentAccountId();
    const bookId = activeBookId.value;
    if (!accountId || !bookId) return;

    try {
      const payload = {
        name: String(chapterName ?? "").trim(),
      };
      await createBookChapter(accountId, bookId, payload);
      chapterActions.closeAddDialog();
      await loadChapters(bookId);
      await syncBookEditorProps();
    } catch (e) {
      const apiMessage =
        e?.response?.data?.name?.[0] ||
        e?.response?.data?.detail ||
        e?.message ||
        "Unable to add chapter.";
      chapterState.value.errorMessage = String(apiMessage);
    }
  },
};

onMounted(async () => {
  try {
    await loadBooks({ page: 1, name: searchQuery.value });
    if (activeBookId.value) {
      await loadBookDetail(activeBookId.value);
    }
  } catch (e) {
    console.error("error loading books: ", e);
  }
});
</script>
<style>
.procedures-page {
  width: 100%;
  background-color: #f2f2f2;
}

.procedures-page-shell {
  width: min(100%, 1259px);
  margin: 0 auto;
}

.procedures-page-shell--search {
  width: 100%;
}

.procedures-page-shell--content {
  margin-top: 43px;
}

.procedures-page-searchbar {
  position: relative;
  z-index: 2;
  width: 100%;
}

@media (max-width: 1600px) {
  .procedures-page-shell {
    width: 100%;
  }
}
</style>
